import { useEffect, useRef } from 'react';

/*
 * Keyboard/a11y helpers (audit 2026-08-14).
 *
 * interactiveRow(fn) — spreads onto a clickable row div/tr so it joins
 * the tab order and activates on Enter/Space like a real button.
 *
 * useDialogRef(onClose) — retrofit for overlay panels: focuses the
 * panel on mount, closes on document-level Escape, and returns focus
 * to the opener on unmount. Pair with role="dialog" aria-modal.
 */

export function interactiveRow(onActivate: () => void) {
    return {
        role: 'button' as const,
        tabIndex: 0,
        onClick: onActivate,
        onKeyDown: (e: React.KeyboardEvent) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onActivate();
            }
        },
    };
}

export function useDialogRef<T extends HTMLElement = HTMLDivElement>(
    open: boolean,
    onClose: () => void,
) {
    const ref = useRef<T>(null);
    useEffect(() => {
        if (!open) return;
        const opener = document.activeElement as HTMLElement | null;
        ref.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('keydown', onKey);
            opener?.focus?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    return ref;
}

/* spread onto the dialog panel alongside the ref */
export function dialogPanelProps(label: string) {
    return {
        role: 'dialog' as const,
        'aria-modal': true,
        'aria-label': label,
        tabIndex: -1,
    };
}
